import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { TableColumn } from '../types/responsiveTable';

export async function saveWorkbook(workbook: XLSX.WorkBook, fileName: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    // En app nativa: guardar en filesystem y compartir
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'base64' });

    const result = await Filesystem.writeFile({
      path: `${fileName}.xlsx`,
      data: excelBuffer,
      directory: Directory.Cache,
    });

    await Share.share({
      title: `${fileName}.xlsx`,
      url: result.uri,
    });
  } else {
    // En navegador: usar file-saver
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    saveAs(blob, `${fileName}.xlsx`);
  }
}

/**
 * Convierte los datos filtrados a formato Excel y lo descarga
 * @param data Datos a exportar
 * @param columns Definición de columnas para extraer los valores correctamente
 * @param fileName Nombre del archivo a descargar
 */
export async function exportToExcel<T extends Record<string, any>>(
  data: T[],
  columns: TableColumn<T>[],
  fileName: string,
): Promise<void> {
  if (!data || data.length === 0) return;

  // Preparar los datos para Excel
  const exportData = data.map((row) => {
    const rowData: Record<string, any> = {};

    columns.forEach((column) => {
      if (column.type === 'avatar' || column.hideColumn) return; // Omitir columnas tipo avatar o ocultas

      const key = column.label;
      let value: any;

      switch (column.type) {
        case 'boolean': {
          value = column.valueFromCallBack
            ? column.valueFromCallBack(row[column.accessor], row)
              ? column.trueText || 'Sí'
              : column.falseText || 'No'
            : row[column.accessor]
              ? column.trueText || 'Sí'
              : column.falseText || 'No';
          break;
        }
        case 'tag': {
          value = column.textKeyFromCallBack ? column.textKeyFromCallBack(row[column.accessor], row) : row[column.accessor];
          break;
        }
        case 'dropdown': {
          const dropdownValues = column.valueFromCallBack
            ? column.valueFromCallBack(row[column.accessor], row)
            : row[column.accessor];
          value = Array.isArray(dropdownValues) ? dropdownValues.join(', ') : dropdownValues;
          break;
        }
        case 'progress': {
          const percentage = column.percentageValueFromCallBack
            ? column.percentageValueFromCallBack(row[column.accessor], row)
            : row[column.accessor];
          value = percentage !== null ? `${Math.round(percentage)}%` : '-';
          break;
        }
        case 'calendar': {
          value = column.valueFromCallBack
            ? column.valueFromCallBack(row[column.accessor], row)
            : row[column.accessor]
              ? new Date(row[column.accessor]).toLocaleDateString('es-ES')
              : '-';
          break;
        }
        case 'number': {
          value = column.valueFromCallBack ? column.valueFromCallBack(row[column.accessor], row) : row[column.accessor];
          // Si es un valor numérico con formato de moneda, lo mantenemos como string
          if (typeof value === 'string' && value.indexOf('€') !== -1) {
            value = String(value).trim();
          }
          break;
        }
        default: {
          value = column.valueFromCallBack ? column.valueFromCallBack(row[column.accessor], row) : row[column.accessor];
          break;
        }
      }

      rowData[key] = value !== undefined && value !== null ? value : '-';
    });

    return rowData;
  });

  // Crear libro y hoja de Excel
  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');

  // Ajustar anchura de columnas
  const columnsWidth = Object.keys(exportData[0] || {}).map(() => ({ wch: 20 }));
  worksheet['!cols'] = columnsWidth;

  await saveWorkbook(workbook, fileName);
}
